import clsx from 'clsx';
import posthog from 'posthog-js';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BrowserRouter, useNavigate, useSearchParams } from 'react-router';
import { VoiceSession } from './VoiceSession';

import { useQuery } from '@tanstack/react-query';
import { Session, VoiceRoleplaySession } from '../../../types/session';
import api from '../../../utils/ky';

// Helper function to format time
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const DurationTimer = ({
  startTimeRef,
}: {
  startTimeRef: React.RefObject<number>;
}) => {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const updateDuration = () => {
      if (startTimeRef.current && startTimeRef.current > 0) {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      } else {
        setDuration(0);
      }
    };
    updateDuration();
    const interval = setInterval(updateDuration, 1000);

    return () => clearInterval(interval);
  }, [startTimeRef.current]);

  return <div className="text-sm text-gray-500">{formatTime(duration)}</div>;
};

// Inner component that uses router hooks
function VoiceRoleplayContent() {
  const navigate = useNavigate();
  // Get sessionId from query params
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const [leavePageWarning, setLeavePageWarning] = useState(false);
  const [session, setSession] = useState<VoiceRoleplaySession | null>(null);
  // Add state for selected language
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<
    string | null
  >(null);

  const { data, isFetching, isError, refetch } = useQuery({
    // Include language in query key to trigger refetch when it changes
    queryKey: ['roleplay', 'session', sessionId, selectedLanguageCode],
    queryFn: async () => {
      // Add language parameter to the URL if available
      const url = selectedLanguageCode
        ? `api/sessions/${sessionId}?language=${selectedLanguageCode}`
        : `api/sessions/${sessionId}`;

      console.log(`Fetching session with URL: ${url}`);
      const session: Session = await api
        .get(url, {
          timeout: 30000, // 30 second timeout
        })
        .json();

      console.log(`Received session with language: ${session.language}`);

      posthog.capture('roleplay_loaded', {
        medium: session?.roleplay?.medium,
        sessionId: session?.id,
        language: session?.language || 'en',
      });

      // Store the session data
      setSession(session as VoiceRoleplaySession);
      return session;
    },
    enabled: !!sessionId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: 1000,
  });

  const startTimeRef = useRef<number>(0);

  // Check if we have stored state on mount
  useEffect(() => {
    if (session && session.id) {
      try {
        const storedState = sessionStorage.getItem(`voice-state-${session.id}`);
        if (storedState) {
          const parsedState = JSON.parse(storedState);
          if (parsedState.startTime && startTimeRef.current === 0) {
            startTimeRef.current = parsedState.startTime;
          }
        }
      } catch (error) {
        console.error('Error retrieving stored state:', error);
      }
    }
  }, [session]);

  // Add language change handler
  const handleLanguageChange = useCallback(
    async (languageCode: string) => {
      console.log(
        `Language changed to ${languageCode}, re-fetching session...`,
      );
      setSelectedLanguageCode(languageCode);

      try {
        // Wait for refetch to complete and log results
        const result = await refetch();

        if (result.data) {
          console.log(
            `Session refetched successfully with language: ${result.data.language}`,
          );

          // Force update the session object to trigger the useEffect in VoiceSession
          setSession({ ...result.data } as VoiceRoleplaySession);
        }
      } catch (error) {
        console.error('Error refetching session:', error);
      }
    },
    [refetch],
  );

  // Show loading state
  if (isFetching || !session) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mx-auto"></div>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center text-red-600">
          <p>Failed to load session data.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-gray-200 rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <main className={clsx('relative flex h-full flex-col')}>
        <div
          className={clsx(
            'sticky top-0 flex w-full items-center justify-between border-b border-[#E0E0E0] px-6 py-3.5',
            'z-50 bg-white',
          )}
        >
          <div className="flex flex-1 items-center gap-2">
            <img
              src="/logos/Hupo_Icon_Orange.svg"
              alt="Hupo"
              className="size-7"
            />
            <h2 className="ml-2 text-lg/6 font-bold tracking-tight">
              {session.context.scenario.title}
            </h2>
            {'   '}
            <DurationTimer startTimeRef={startTimeRef} />
          </div>
        </div>

        <VoiceSession
          session={session}
          startTimeRef={startTimeRef}
          leavePageWarning={leavePageWarning}
          setLeavePageWarning={setLeavePageWarning}
          provider={'11labs'}
          onLanguageChange={handleLanguageChange}
        />
      </main>
    </>
  );
}

// Wrapper component that provides Router context
export function VoiceRoleplay() {
  return (
    <BrowserRouter>
      <VoiceRoleplayContent />
    </BrowserRouter>
  );
}
